import { FC, useMemo, useRef } from "react";
import { Button, Col, Form, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { FontVariant, TypographyCategory, TypographySource } from "../../../shared/models/brand.model";
import { FontPicker } from "../../kinema/panels/FontPicker";
import type { AssetKitFont } from "../../kinema/assetKit";
import { BrandTypographyStyleEntry } from "../hooks/useOrganizationBrand";

/*
    The Typography section of the Branding tab behind `brand-identity-v2` (MPN-2033).

    Each style names a Font Family for one Category of text. The family is either from Google
    Fonts, picked in the same picker Create uses, or one of the brand's uploaded fonts (the
    Fonts section above). A style holds no weight or italic: Create's Bold/Italic set those.
*/

interface Props {
  styles: BrandTypographyStyleEntry[];
  setStyles: (update: (prev: BrandTypographyStyleEntry[]) => BrandTypographyStyleEntry[]) => void;
  /** The brand's uploaded fonts; their families are what an Uploaded style may name. */
  fonts: FontVariant[];
  onSave: () => Promise<void>;
  isLoading: boolean;
}

const CATEGORIES: { value: TypographyCategory; label: string }[] = [
  { value: "heading", label: "Heading" },
  { value: "display", label: "Display" },
  { value: "body", label: "Body" },
  { value: "caption", label: "Caption" },
  { value: "quote", label: "Quote" },
  { value: "button", label: "Button" },
];

// Google mode lists only the Google catalog; the brand's own fonts are the Uploaded choice.
const NO_BRAND_FONTS: AssetKitFont[] = [];

const newStyle = (key: string): BrandTypographyStyleEntry => ({
  key,
  id: null,
  name: "",
  category: "heading",
  family_name: "",
  source: "google",
  usage_guidelines: "",
});

/** One entry per family, whatever its weights; the server matches families case-insensitively. */
const uploadedFamilies = (fonts: FontVariant[]) => {
  const seen = new Map<string, string>();
  for (const font of fonts) {
    const key = font.family_name.toLowerCase();
    if (!seen.has(key)) seen.set(key, font.family_name);
  }
  return Array.from(seen.values());
};

const BrandTypographySection: FC<Props> = ({ styles, setStyles, fonts, onSave, isLoading }) => {
  const nextKey = useRef(0);
  const families = useMemo(() => uploadedFamilies(fonts), [fonts]);

  const setField = <K extends keyof BrandTypographyStyleEntry>(key: string, field: K, value: BrandTypographyStyleEntry[K]) => {
    setStyles((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));
  };

  // A Google family isn't an uploaded one, or the other way round, so switching starts the family over.
  const setSource = (key: string, source: TypographySource) => {
    setStyles((prev) => prev.map((s) => (s.key === key && s.source !== source ? { ...s, source, family_name: "" } : s)));
  };

  const addStyle = () => {
    nextKey.current += 1;
    setStyles((prev) => [...prev, newStyle(`new-${nextKey.current}`)]);
  };

  const removeStyle = (key: string) => {
    setStyles((prev) => prev.filter((s) => s.key !== key));
  };

  const uploadedSelect = (entry: BrandTypographyStyleEntry) => {
    // A saved family whose font has since been unlinked is still shown, so the select never lies.
    const unlinked = !!entry.family_name && !families.some((f) => f.toLowerCase() === entry.family_name.toLowerCase());
    return (
      <>
        <Form.Select
          value={entry.family_name}
          disabled={families.length === 0 && !entry.family_name}
          onChange={(e) => setField(entry.key, "family_name", e.target.value)}
        >
          <option value="">Select an uploaded font...</option>
          {families.map((family) => (
            <option key={family} value={family}>
              {family}
            </option>
          ))}
          {unlinked && <option value={entry.family_name}>{entry.family_name} (not linked)</option>}
        </Form.Select>
        {families.length === 0 && (
          <div className="small text-muted mt-1">Upload a font in the Fonts section first.</div>
        )}
      </>
    );
  };

  return (
    <div className="brand-section">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h6 className="brand-section-title">Typography</h6>
          <p className="brand-section-hint">Named font styles, applied to text layers in Create.</p>
        </div>
        <span className="brand-section-count">
          {styles.length} {styles.length === 1 ? "style" : "styles"}
        </span>
      </div>

      {styles.map((entry, i) => {
        const label = entry.name || `Style ${i + 1}`;
        return (
          <div key={entry.key} className="brand-swatch brand-typography-style mt-3" role="group" aria-label={label}>
            <div className="brand-swatch-body border-top-0">
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-2" controlId={`style-${entry.key}-name`}>
                    <Form.Label className="small fw-semibold mb-1">Style Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={entry.name}
                      onChange={(e) => setField(entry.key, "name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2" controlId={`style-${entry.key}-category`}>
                    <Form.Label className="small fw-semibold mb-1">Category</Form.Label>
                    <Form.Select
                      value={entry.category}
                      onChange={(e) => setField(entry.key, "category", e.target.value as TypographyCategory)}
                    >
                      {CATEGORIES.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <div className="small fw-semibold mb-1">Source</div>
                  <ToggleButtonGroup
                    type="radio"
                    size="sm"
                    className="mb-2"
                    name={`style-${entry.key}-source`}
                    value={entry.source}
                    onChange={(source: TypographySource) => setSource(entry.key, source)}
                  >
                    <ToggleButton id={`style-${entry.key}-google`} variant="outline-primary" value="google">
                      Google
                    </ToggleButton>
                    <ToggleButton id={`style-${entry.key}-uploaded`} variant="outline-primary" value="uploaded">
                      Uploaded
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Col>
                <Col md={1} className="d-flex align-items-end justify-content-end mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remove ${label}`}
                    onClick={() => removeStyle(entry.key)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2" controlId={`style-${entry.key}-family`}>
                    <Form.Label className="small fw-semibold mb-1">Font Family</Form.Label>
                    {entry.source === "google" ? (
                      <div className="d-flex">
                        <FontPicker
                          value={entry.family_name}
                          brandFonts={NO_BRAND_FONTS}
                          onChange={(family) => setField(entry.key, "family_name", family)}
                        />
                      </div>
                    ) : (
                      uploadedSelect(entry)
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2" controlId={`style-${entry.key}-usage`}>
                    <Form.Label className="small fw-semibold mb-1">Usage Guidelines</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={entry.usage_guidelines}
                      onChange={(e) => setField(entry.key, "usage_guidelines", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {entry.error && (
                <div className="small text-danger" role="alert">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {entry.error}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="d-flex justify-content-between mt-3">
        <Button variant="outline-primary" size="sm" onClick={addStyle} disabled={isLoading}>
          <i className="bi bi-plus-lg me-1"></i>
          Add Style
        </Button>
        <Button variant="primary" size="sm" onClick={() => onSave()} disabled={isLoading}>
          Save Typography
        </Button>
      </div>
    </div>
  );
};

export default BrandTypographySection;
